import React from 'react'
import { addNotification, useDispatch, useSelector } from '@ggs/store'
import { useI18n } from '@ggs/i18n'
import { get, omit, size, reject, find, split, isArray } from 'lodash'
import { getDrupalSetting } from '@ggs/utils'
import { setDrupalToken } from '@ggs/store/actions/session'
import { useApiRequest } from './'

/**
 * @typedef {import("../../definitions").Jsonapi} Jsonapi
 * @typedef {import("../../definitions").ProjectNode} ProjectNode
 * @typedef {import("../../definitions").ProductNode} ProductNode
 * @typedef {import("../../definitions").ResourceMedia} ResourceMedia
 * @typedef {import("../../definitions").FabricNode} FabricNode
 * @typedef {import("../../definitions").CustomProductMedia} CustomProductMedia
 * @typedef {import("../../definitions").CollectionNode} CollectionNode
 * @typedef {import("../../definitions").GenericEntity} GenericEntity
 * @typedef {import("../../definitions").Relationships} Relationships
 * @typedef {import("../../definitions").ResourceRelationship} ResourceRelationship
 * @typedef {import("../../definitions").CustomProductMediaPayload} CustomProductMediaPayload
 */

/**
 * @typedef {(name: string, description: string) => Promise<ProjectNode>} CreateProject
 * @typedef {(project: ProjectNode) => Promise<ProjectNode>} UpdateProject
 * @typedef {(projectId: string) => void} DeleteProject
 * @typedef {(project: ProjectNode, resource: GenericEntity) => Promise<ProjectNode>} AddResourceToProject
 * @typedef {(project: ProjectNode, resource: GenericEntity) => Promise<ProjectNode>} RemoveResourceFromProject
 * @typedef {(project: ProjectNode, resource: GenericEntity) => Promise<ProjectNode>} SetResourceAsProjectCover
 * @typedef {(productId: string) => Promise<ProductNode>} GetProduct
 * @typedef {(resourceId: string) => Promise<ResourceMedia>} GetResource
 * @typedef {(resourceId: string) => Promise<ResourceMedia>} getCustomProduct
 * @typedef {(projectId: string) => Promise<ProjectNode>} GetProject
 * @typedef {(fabricId: string) => Promise<FabricNode>} GetFabric
 * @typedef {(collectionId: string) => Promise<CollectionNode>} GetCollection
 * @typedef {(userId: string) => Promise<ProjectNode[]>} GetOwnProjects
 * @typedef {(payload: CustomProductMediaPayload) => Promise<CustomProductMedia>} CreateCustomProductMedia
 * @typedef {() => Promise<string>} AcquireDrupalToken
 */

/**
 * @typedef {Object} DrupalApiHook
 * @property {CreateProject} createProject
 * @property {UpdateProject} updateProject
 * @property {AddResourceToProject} addResourceToProject
 * @property {RemoveResourceFromProject} removeResourceFromProject
 * @property {SetResourceAsProjectCover} setResourceAsProjectCover
 * @property {GetProduct} getProduct
 * @property {GetResource} getResource
 * @property {getCustomProduct} getCustomProduct
 * @property {GetCollection} getCollection
 * @property {GetFabric} getFabric
 * @property {GetOwnProjects} getOwnProjects
 * @property {GetProject} getProject
 * @property {DeleteProject} deleteProject
 * @property {AcquireDrupalToken} acquireDrupalToken
 * @property {CreateCustomProductMedia} createCustomProductMedia
 */

/**
 * Hook that expose utilities to retrieve Drupal JSON API data.
 * @return {DrupalApiHook}
 */
function useDrupalApi() {
  const { t } = useI18n()
  const dispatch = useDispatch()
  const baseUrl =
    getDrupalSetting('baseUrl') || `${process.env.REACT_APP_DRUPAL_BASE_URL}` || 'http://localhost'
  const basePath = '/jsonapi'
  const authToken = `${process.env.REACT_APP_DRUPAL_API_TOKEN}`
  const isStorybook = `${process.env.STORYBOOK}` === 'yes'
  const drupalToken = useSelector((state) => state.session.drupalToken)
  const { doApiRequest, cleanCacheItem } = useApiRequest({
    baseUrl,
    authToken,
    csrfToken: drupalToken,
  })

  /**
   * Negotiate to get a Drupal API CSRF token.
   * @return {Promise<string>}
   */
  const acquireDrupalToken = async () => {
    if (!isStorybook) {
      const token = await getSessionToken()
      const rawToken = token.toString()
      if (size(rawToken) > 10) {
        dispatch(setDrupalToken(rawToken))
      }

      return rawToken
    }
  }

  /**
   * Check current user active Drupal session.
   */
  const checkActiveSession = async () => {
    const response = await doRequest(basePath)
    const userUUID = get(response, 'meta.links.me.href', null)

    return Boolean(userUUID)
  }

  /**
   * Process a Drupal API endpoint request.
   * @param {string} endpoint An API endpoint path
   * @param {any} data Payload data
   * @param {Object<string, string>=} customHeaders
   * @param {string} method
   * @param {Object<string, string|boolean>=} extraOptions
   * @return {Promise<Jsonapi>}
   */
  const doRequest = async (
    endpoint,
    data = null,
    customHeaders,
    method = '',
    extraOptions = {}
  ) => {
    const response = doApiRequest({
      endpoint,
      data,
      customHeaders,
      method,
      extraOptions,
    })

    const errorCode = get(response, 'errorCode', null)
    if (errorCode === 403) {
      const hasValidSession = await checkActiveSession()
      if (hasValidSession) {
        // Session is valid but user don't have privilege granted for the consumed endpoint.
        dispatch(
          addNotification(
            'drupal_api_request_failed',
            'error',
            t('Drupal API request was forbidden for current user.')
          )
        )
      } else {
        // Session is expired, user need to authenticate again, redirect to Drupal login.
        window.location.href = `${baseUrl}/user`
      }

      return null
    }

    return response
  }

  /**
   * Acquire a Drupal session token.
   */
  const getSessionToken = async () => {
    const endpoint = '/session/token'
    const response = await doRequest(endpoint, {}, {}, 'POST')
    return response.data || ''
  }

  /**
   * Normalize resource item as entity object.
   * @param {Object<string, any>} item
   * @return {any}
   */
  const normalizeEntity = (item) => {
    return {
      ...item.attributes,
      type: item.type,
      id: item.id,
      relationships: item.relationships,
    }
  }

  /**
   * Create a new project.
   *
   * @type {CreateProject}
   * @param {string} projectName
   * @param {string} projectDescription
   * @return {Promise<ProjectNode>}
   */
  const createProject = async (projectName, projectDescription) => {
    const endpoint = `${basePath}/node/project`
    const payload = {
      data: {
        type: 'node--project',
        attributes: {
          title: projectName,
          description: {
            value: projectDescription,
            format: 'plain_text',
          },
        },
        relationships: {
          resources: {},
        },
      },
    }

    const response = await doRequest(endpoint, payload)

    /**
     * @type {ProjectNode}
     */
    const node = get(response, 'data.attributes', null)
    const nodeId = get(response, 'data.id', null)

    if (!node || !nodeId) {
      return null
    }

    cleanCacheItem('own-projects-list')

    return {
      id: nodeId,
      ...node,
    }
  }

  /**
   * Update a project.
   *
   * @type {UpdateProject}
   * @param {ProjectNode} projectNode
   * @return {Promise<ProjectNode>}
   */
  const updateProject = async (projectNode) => {
    const projectId = projectNode?.id || ''
    const endpoint = `${basePath}/node/project/${projectId}`
    const payload = {
      data: {
        type: 'node--project',
        id: get(projectNode, 'id', ''),
        relationships: get(projectNode, 'relationships', []),
        attributes: {
          ...omit(projectNode, ['id', 'relationships', 'type']),
        },
      },
    }

    const response = await doRequest(endpoint, payload, {}, 'PATCH')

    /**
     * @type {ProjectNode}
     */
    const node = get(response, 'data.attributes', null)
    const nodeId = get(response, 'data.id', null)

    if (!node || !nodeId) {
      return null
    }

    return {
      id: nodeId,
      ...node,
    }
  }

  /**
   * @typedef {Object} ProjectDataPayload
   * @property {ProjectNode} data
   */

  /**
   * Compose project resource relationships payload.
   *
   * @param {ProjectNode} project
   * @param {ResourceRelationship[]} resources
   * @return {ProjectDataPayload}
   */
  const composeProjectResourcePayload = (project, resources) => {
    return {
      data: {
        ...project,
        type: 'node--project',
        relationships: {
          resources: {
            data: resources,
          },
        },
      },
    }
  }

  /**
   * Process project entity update.
   *
   * @param {ProjectNode} project
   * @param {ResourceRelationship[]} projectResources
   * @return {Promise<ProjectNode>}
   */
  const processProjectUpdate = async (project, projectResources) => {
    const endpoint = `${basePath}/node/project/${project.id}`
    const response = await doRequest(
      endpoint,
      composeProjectResourcePayload(project, projectResources),
      {},
      'PATCH'
    )

    const entity = get(response, 'data', null)
    return entity ? normalizeEntity(entity) : null
  }

  /**
   * Add resource to existing project.
   *
   * @type {AddResourceToProject}
   * @param {ProjectNode} project
   * @param {GenericEntity} resource
   * @return {Promise<ProjectNode>}
   */
  const addResourceToProject = async (project, resource) => {
    const projectResourcesRelationships = get(project, 'relationships.resources.data', [])
    const [entity_type] = split(resource.type, '--')
    const resourceMatch = find(projectResourcesRelationships, { id: resource.id })

    if (resourceMatch) {
      dispatch(
        addNotification(
          'add_resource_duplication_reject',
          'warning',
          t('Resource "{{title}}" already exists in project resources.', {
            title: get(resource, 'title', get(resource, 'name', resource.id)),
          })
        )
      )

      throw new Error('Resource already exists in project.')
    }

    projectResourcesRelationships.push({
      type: resource.type,
      id: resource.id,
      meta: {
        target_type: entity_type,
      },
    })

    return await processProjectUpdate(project, projectResourcesRelationships)
  }

  /**
   * Set resource as project cover.
   *
   * @type {AddResourceToProject}
   * @param {ProjectNode} project
   * @param {GenericEntity} resource
   * @return {Promise<ProjectNode>}
   */
  const setResourceAsProjectCover = async (project, resource) => {
    const projectResources = get(project, 'relationships.resources.data', [])
    // Filter resource reference if already referenced.
    const filteredProjectResources = /** @type {ResourceRelationship[]} */ reject(
      projectResources,
      { id: resource.id }
    )
    const [entity_type] = split(resource.type, '--')

    filteredProjectResources.unshift({
      type: resource.type,
      id: resource.id,
      meta: {
        target_type: entity_type,
      },
    })

    return await processProjectUpdate(project, filteredProjectResources)
  }

  /**
   * Remove resource from existing project.
   *
   * @type {RemoveResourceFromProject}
   * @param {ProjectNode} project
   * @param {GenericEntity} resource
   * @return {Promise<ProjectNode>}
   */
  const removeResourceFromProject = async (project, resource) => {
    const projectResources = get(project, 'relationships.resources.data', [])
    // Clean reference of removed resource.
    const filteredProjectResources = /** @type {ResourceRelationship[]} */ reject(
      projectResources,
      { id: resource.id }
    )

    return await processProjectUpdate(project, filteredProjectResources)
  }

  /**
   * Get project by Node ID.
   * @param {string} projectNid Project Node ID.
   * @return {Promise<ProjectNode>}
   */
  const getProject = async (projectNid) => {
    const endpoint = `${basePath}/node/project`
    const params = `filter[drupal_internal__nid]=${projectNid}`
    const response = await doRequest(endpoint + '?' + encodeURI(params), {}, null, 'GET', {
      cache: false,
    })

    const entity = get(response, 'data[0]', null)
    return entity ? normalizeEntity(entity) : null
  }

  /**
   * Delete project by Node UUID.
   * @type {DeleteProject}
   * @param {string} projectNid Project Node ID.
   * @return {Promise<void>}
   */
  const deleteProject = async (projectNid) => {
    // Resolve project using Node ID to determine UUID.
    const projectNode = await getProject(projectNid)

    if (!projectNode) {
      throw new Error(`Node with NID ${projectNid} cannot be resolved.`)
    }

    const endpoint = `${basePath}/node/project/${projectNode.id}`
    await doRequest(endpoint, null, {}, 'DELETE')
    cleanCacheItem('own-projects-list')
  }

  /**
   * Get product by node id.
   * @param {string} productId product node id.
   * @return {Promise<ProductNode>}
   */
  const getProduct = async (productId) => {
    const endpoint = `${basePath}/node/product`
    const params = `filter[drupal_internal__nid]=${productId}`
    const response = await doRequest(endpoint + '?' + encodeURI(params))
    const entity = get(response, 'data[0]', null)
    return entity ? normalizeEntity(entity) : null
  }

  /**
   * Get resource by Media ID.
   * @param {string} resourceId Resource Media ID.
   * @return {Promise<ResourceMedia>}
   */
  const getResource = async (resourceId) => {
    const endpoint = `${basePath}/media/resource`
    const params = `filter[drupal_internal__mid]=${resourceId}`
    const response = await doRequest(endpoint + '?' + encodeURI(params))
    const entity = get(response, 'data[0]', null)
    return entity ? normalizeEntity(entity) : null
  }
  /**
   * Get resource by Media ID.
   * @param {string} resourceId Resource Media ID.
   * @return {Promise<ResourceMedia>}
   */
  const getCustomProduct = async (resourceId) => {
    const endpoint = `${basePath}/media/custom_product`
    const params = `filter[drupal_internal__mid]=${resourceId}`
    const response = await doRequest(endpoint + '?' + encodeURI(params))
    const entity = get(response, 'data[0]', null)
    return entity ? normalizeEntity(entity) : null
  }

  /**
   * Get fabric by Node ID.
   * @param {string} fabricId Fabric Node ID.
   * @return {Promise<FabricNode>}
   */
  const getFabric = async (fabricId) => {
    const endpoint = `${basePath}/node/fabric`
    const params = `filter[drupal_internal__nid]=${fabricId}`
    const response = await doRequest(endpoint + '?' + encodeURI(params))
    const entity = get(response, 'data[0]', null)
    return entity ? normalizeEntity(entity) : null
  }

  /**
   * Get collection by Node ID.
   * @param {string} collectionId Collection Node ID.
   * @return {Promise<CollectionNode>}
   */
  const getCollection = async (collectionId) => {
    const endpoint = `${basePath}/node/collection`
    const params = `filter[drupal_internal__nid]=${collectionId}`
    const response = await doRequest(endpoint + '?' + encodeURI(params))
    const entity = get(response, 'data[0]', null)
    return entity ? normalizeEntity(entity) : null
  }

  /**
   * Get owned project nodes.
   * @type {GetOwnProjects}
   * @param {string} userId Drupal user UUID.
   * @return {Promise<ProjectNode[]>}
   */
  const getOwnProjects = async (userId) => {
    const endpoint = `${basePath}/node/project`
    const params = `filter[uid.id]=${userId}`
    const response = await doRequest(endpoint + '?' + encodeURI(params), {}, null, 'GET', {
      id: 'own-projects-list',
    })
    const items = get(response, 'data', null)

    if (!items || !isArray(items)) {
      return null
    }

    // Normalize as nodes array data structure.
    return items.map(normalizeEntity)
  }

  /**
   * Create media custom product entity.
   * @type {CreateCustomProductMedia}
   * @param {CustomProductMediaPayload} payload
   * @return {Promise<CustomProductMedia>}
   */
  const createCustomProductMedia = async (payload) => {
    const endpoint = '/api/custom-product/create'
    const response = await doRequest(endpoint, payload, {}, 'POST')
    return get(response, 'media', null)
  }

  return {
    acquireDrupalToken,
    addResourceToProject,
    createProject,
    createCustomProductMedia,
    deleteProject,
    getCollection,
    getFabric,
    getOwnProjects,
    getProduct,
    getProject,
    getResource,
    getCustomProduct,
    removeResourceFromProject,
    setResourceAsProjectCover,
    updateProject,
  }
}

export default useDrupalApi
